export enum UserRole {
  CUSTOMER = 'CUSTOMER',
  SHOP_OWNER = 'SHOP_OWNER',
  STAFF = 'STAFF',
  ADMIN = 'ADMIN'
}

export type Language = 'EN' | 'KH' | 'ZH' | 'JA' | 'KO';

export enum JobStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  REJECTED = 'REJECTED',
  DIAGNOSING = 'DIAGNOSING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export interface Transaction {
  id: string;
  shopId: string;
  type: 'INCOME' | 'EXPENSE';
  category: string;
  amount: number;
  description: string;
  date: string;
}

export interface Shop {
  id: string;
  owner_id?: string;
  name: string;
  category: string[];
  rating: number;
  address: string;
  imageUrl: string;
  logoUrl: string;
  description: string;
  contact: {
    phone: string;
    telegram?: string;
    email?: string;
  };
  workingHours: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  vatPercentage: number;
}

export interface Profile {
  id: string;
  role: UserRole;
  full_name: string;
  email: string;
  avatar_url?: string;
  created_at?: string;
}

export interface Staff {
  id: string;
  name: string;
  email: string;
  role: string;
  skills: string[];
  shopId: string;
  avatarUrl: string;
}

export interface ServiceItem {
  id: string;
  name: string;
  price: string;
}

export interface Service {
  id: string;
  shopId: string;
  name: string;
  category: string;
  items: ServiceItem[];
  description?: string;
  imageUrl?: string;
  status: 'PUBLIC' | 'PRIVATE';
}

export interface Discount {
  id: string;
  shopId: string;
  name: string;
  description: string;
  type: 'SIMPLE' | 'GROUP' | 'PERIOD' | 'CATEGORY' | 'LOYALTY';
  valueType: 'PERCENTAGE' | 'FIXED';
  value: number;
  startDate?: string;
  endDate?: string;
  minGroupSize?: number;
  targetCategories?: string[]; 
  minBookings?: number;       
  status: 'ACTIVE' | 'INACTIVE';
}

export interface ChatMessage {
  id: string;
  senderId: string;
  senderRole: UserRole;
  senderName: string;
  text: string;
  translatedText?: string;
  timestamp: string;
  isShopSide: boolean;
}

export interface WorkTask {
  id: string;
  description: string;
  cost: number;
  isFromPackage: boolean;
  loggedAt: string;
  staffName: string;
  staffId?: string;
}

export interface InvoiceItem {
  description: string;
  amount: number;
}

export interface Invoice {
  id: string;
  items: InvoiceItem[];
  subtotal: number;
  discountAmount: number;
  vatAmount: number;
  totalAmount: number;
  status: 'UNPAID' | 'PAID';
  createdAt: string;
}

export interface PaymentProof {
  imageUrl: string;
  submittedAt: string;
  status: 'PENDING' | 'VERIFIED' | 'REJECTED';
}

export interface LocationData {
  lat?: number;
  lng?: number;
  mapUrl?: string;
  addressHint?: string;
}

export interface RepairJob {
  id: string;
  shopId: string;
  shopName: string;
  customerId: string;
  customerName: string;
  contactMethod: 'Phone' | 'Telegram';
  contactValue: string;
  description: string;
  aiDiagnosis?: AIDiagnosis;
  status: JobStatus;
  createdAt: string;
  preferredSlots: string[]; 
  scheduledDate: string; 
  estimatedCost?: string;
  assignedStaffIds: string[];
  workTasks: WorkTask[];
  invoice?: Invoice;
  paymentProof?: PaymentProof;
  customerLocation: LocationData;
  messages: ChatMessage[];
  isRated: boolean;
}

export interface AIDiagnosis {
  summary: string;
  likelyIssue: string;
  severity: 'Low' | 'Medium' | 'High' | 'Critical';
  estimatedPriceRange: string;
  suggestedAction: string;
}

export interface Rating {
  id: string;
  shopId: string;
  jobId: string;
  customerId: string;
  customerName: string;
  stars: number;
  comment: string;
  createdAt: string;
}

export type ReminderType = 'GUARANTEE' | 'OIL_CHANGE' | 'MAINTENANCE' | 'TAX' | 'CUSTOM';

export interface Reminder {
  id: string;
  customerId: string;
  title: string;
  description: string;
  type: ReminderType;
  dueDate: string;
  remindAt: string;
  isCompleted: boolean;
  category: string;
}

export type ViewState = 
  | 'AUTH' 
  | 'HOME' 
  | 'SHOP_PROFILE' 
  | 'BOOKINGS' 
  | 'BOOKING_DETAIL'
  | 'REMINDERS'
  | 'PROFILE' 
  | 'SHOP_DASHBOARD' 
  | 'SHOP_FINANCE' 
  | 'SHOP_SERVICES'
  | 'ADMIN_DASHBOARD' 
  | 'ADMIN_SHOPS' 
  | 'ADMIN_USERS'
  | 'SHOP_SETTINGS'
  | 'COMMUNITY_LICENSE'
  | 'MANUAL'
  | 'ONBOARDING';